import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Pencil1Icon, TrashIcon, ReloadIcon } from '@/shared/ui/radix-icons';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { eventColor } from '../event-color';
import { calendarDisplayName } from '@features/agenda/utils/calendar-display';
import { eventTimeLabel } from '../time-grid';
import { Text, UnstyledButton } from '@/shared/ui';

interface MobileEventPreviewSheetProps {
  event: ExpandedEvent;
  calendars: Calendar[];
  canEdit: boolean;
  onEdit: (event: ExpandedEvent) => void;
  onDelete: (event: ExpandedEvent) => void;
  onClose: () => void;
}

/**
 * Mobile bottom-sheet for previewing event details. Shows title, time,
 * calendar, description and offers edit/delete actions gated by permission.
 *
 * @param props Event data, calendars, permission flag and handlers.
 * @returns Rendered mobile event preview sheet.
 */
export const MobileEventPreviewSheet: React.FC<MobileEventPreviewSheetProps> = ({
  event,
  calendars,
  canEdit,
  onEdit,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation('features/agenda');
  const color = eventColor(event, calendars);
  const calendar = calendars.find((c) => c.calendar_id === event.calendar_id);
  const calName = calendar ? calendarDisplayName(calendar, t) : t('preview.unknownCalendar');
  const recurring = Array.isArray(event.recurrence) && event.recurrence.length > 0;
  const duration = dayjs(event.end_at_utc).diff(dayjs(event.start_at_utc), 'minute');

  return (
    <div
      data-testid="mobile-event-preview-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={event.title}
      className="fixed inset-0 z-50 flex flex-col"
    >
      {/* Scrim */}
      <UnstyledButton
        type="button"
        aria-label={t('mobile.close_sheet')}
        onClick={onClose}
        className="absolute inset-0 bg-[var(--overlay-scrim)]"
      >
        {null}
      </UnstyledButton>

      {/* Sheet */}
      <div
        className="relative z-10 mt-auto rounded-t-[var(--radius-xl)] bg-[var(--surface-primary)] px-(--space-4) pt-(--space-4) shadow-[var(--shadow-lg)]"
        style={{ paddingBottom: 'calc(var(--space-6) + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Grabber */}
        <Text
          component="span"
          aria-hidden="true"
          className="mx-auto mb-(--space-4) block h-1 w-10 rounded-full bg-[var(--border-subtle)]"
        />

        {/* Color indicator + Title */}
        <div className="mb-(--space-4) flex items-start gap-(--space-3)">
          <Text
            component="span"
            aria-hidden="true"
            className="mt-(--space-1) h-(--space-3-5) w-(--space-3-5) shrink-0 rounded-full"
            style={{ background: color }}
          />
          <div className="min-w-0 flex-1">
            <Text className="text-(--text-h6) font-bold text-[var(--text-primary)] leading-snug">
              {event.title}
            </Text>
            {recurring && (
              <Text className="mt-px flex items-center gap-(--space-1) text-xs text-[var(--text-tertiary)]">
                <ReloadIcon width={11} height={11} />
                {t('preview.recurring')}
              </Text>
            )}
          </div>
        </div>

        {/* Detail rows */}
        <div className="mb-(--space-5) flex flex-col gap-(--space-3)">
          <DetailRow
            icon={<ClockIcon />}
            label={eventTimeLabel(event, t('drawer.all_day'))}
            sublabel={t('mobile.event_duration', { count: duration })}
          />
          <DetailRow icon={<CalendarIcon />} label={calName} />
          {event.description?.trim() && (
            <Text className="whitespace-pre-wrap text-(--text-small) text-[var(--text-secondary)]">
              {event.description.trim()}
            </Text>
          )}
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex gap-(--space-3)">
            <UnstyledButton
              type="button"
              onClick={() => onEdit(event)}
              className="flex flex-1 items-center justify-center gap-(--space-1-5) rounded-[var(--radius-md)] py-(--space-3) text-(--text-small) font-semibold text-[var(--button-primary-text)] transition-opacity active:opacity-90"
              style={{ background: 'var(--seed-base)' }}
            >
              <Pencil1Icon width={14} height={14} />
              {t('preview.edit')}
            </UnstyledButton>
            <UnstyledButton
              type="button"
              onClick={() => onDelete(event)}
              className="flex items-center justify-center gap-(--space-1-5) rounded-[var(--radius-md)] border border-[var(--border-subtle)] px-(--space-4) py-(--space-3) text-(--text-small) font-semibold text-[var(--status-error-text)] transition-colors active:bg-[var(--status-error-text)]/10"
            >
              <TrashIcon width={14} height={14} />
              {t('preview.delete')}
            </UnstyledButton>
          </div>
        )}
      </div>
    </div>
  );
};

/* --- helpers --- */

/**
 * @description Renders a labeled detail row with an icon.
 */
function DetailRow({ icon, label, sublabel }: { icon: React.ReactNode; label: string; sublabel?: string }) {
  return (
    <div className="flex items-center gap-(--space-3)">
      <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[var(--text-tertiary)]">
        {icon}
      </div>
      <div>
        <Text className="text-(--text-body-sm) font-medium text-[var(--text-primary)]">
          {label}
        </Text>
        {sublabel && (
          <Text className="text-xs text-[var(--text-tertiary)]">
            {sublabel}
          </Text>
        )}
      </div>
    </div>
  );
}
/**
 * @description Renders a clock SVG icon.
 */

function ClockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 15 15" fill="none">
      <path d="M7.5 0a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15ZM7 4a.5.5 0 0 1 1 0v3.3l2.15 1.28a.5.5 0 0 1-.5.86L7.25 7.8A.5.5 0 0 1 7 7.37V4Z" fill="currentColor" />
    </svg>
  );
/**
 * @description Renders a calendar SVG icon.
 */
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 15 15" fill="none">
      <path d="M4.5 1a.5.5 0 0 1 .5.5V2h5v-.5a.5.5 0 0 1 1 0V2h1.5A1.5 1.5 0 0 1 14 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 1 12.5v-9A1.5 1.5 0 0 1 2.5 2H4v-.5a.5.5 0 0 1 .5-.5ZM2 5v7.5a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V5H2Z" fill="currentColor" />
    </svg>
  );
}
